import { useCallback, useEffect, useMemo, useState } from "react";
import { ProcessStatusType } from "./processStatusPanel";

interface Props {
  timeoutSeconds?: number;
  processingGuardMessage?: string;
}

const useProcessStatusModal = ({
  timeoutSeconds = 0,
  processingGuardMessage = "Process is running. Please do not refresh, close, or go back.",
}: Props = {}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [status, setStatus] = useState<ProcessStatusType>("processing");
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);

  const startProcessing = useCallback(() => {
    setElapsedSeconds(0);
    setStatus("processing");
    setOpen(true);
  }, []);

  const setFailed = useCallback(() => {
    setStatus("failed");
    setOpen(true);
  }, []);

  const setSuccess = useCallback(() => {
    setStatus("success");
    setOpen(true);
  }, []);

  const setDelayed = useCallback(() => {
    setStatus("delayed");
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open || status !== "processing") return;

    setElapsedSeconds(0);
    const elapsedIntervalId = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);

    const blockRefreshOrClose = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = processingGuardMessage;
    };

    const blockBackNavigation = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("beforeunload", blockRefreshOrClose);
    window.addEventListener("popstate", blockBackNavigation);

    return () => {
      clearInterval(elapsedIntervalId);
      window.removeEventListener("beforeunload", blockRefreshOrClose);
      window.removeEventListener("popstate", blockBackNavigation);
    };
  }, [open, status, processingGuardMessage]);

  const countdownLabel = useMemo(() => {
    if (!timeoutSeconds) return "00:00";
    const remaining = Math.max(0, timeoutSeconds - elapsedSeconds);
    const mm = Math.floor(remaining / 60)
      .toString()
      .padStart(2, "0");
    const ss = Math.floor(remaining % 60)
      .toString()
      .padStart(2, "0");
    return `${mm}:${ss}`;
  }, [elapsedSeconds, timeoutSeconds]);

  return {
    open,
    status,
    elapsedSeconds,
    countdownLabel,
    setOpen,
    setStatus,
    startProcessing,
    setFailed,
    setSuccess,
    setDelayed,
    close,
  };
};

export default useProcessStatusModal;
